import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import {
  createEmptyInspectionItem,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/formSerialize';
import {
  REPAIR_REPORT_INSPECTION_RESULT_OPTIONS,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/repairReportStatus';

const ITEM_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(10rem,1.5fr)_minmax(0,9rem)_2.5rem]';

const LIST_MAX_HEIGHT_STYLE = {
  maxHeight: 'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

const LINE_HEADER_CLASS = cn(
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  ITEM_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-center',
  ITEM_GRID_COLS,
);

const RepairReportInspectionItemsEditor = ({
  control,
  register,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'inspectionItems',
  });

  return (
    <section className="@container/form-section">
      <div className="form-section__header">
        <h2 className="form-block__title mr-2">檢測項目</h2>
        <Button
          type="button"
          size="sm"
          onClick={() => append(createEmptyInspectionItem())}
        >
          新增項目
        </Button>
      </div>

      <div className="form-list mt-3" style={LIST_MAX_HEIGHT_STYLE}>
        <div className={LINE_HEADER_CLASS}>
          <span>項目說明</span>
          <span>結果</span>
          <span className="sr-only">移除</span>
        </div>
        <ul className="form-list__body divide-y divide-gray-200">
          {fields.length === 0 ? (
            <li className="px-3 py-8 text-center text-sm text-gray-500">
              尚無檢測項目（完成前至少一筆）
            </li>
          ) : (
            fields.map((field, index) => (
              <li key={field.id} className={LINE_ROW_CLASS}>
                <PlainRegisteredInput
                  id={`inspectionItems.${index}.description`}
                  register={register}
                  errors={errors}
                  placeholder="項目說明"
                  mobileLabel="項目說明"
                  suppressInlineError
                />
                <div className="min-w-0">
                  <ListFieldMobileLabel>結果</ListFieldMobileLabel>
                  <FloatingSelect
                    id={`inspectionItems.${index}.result`}
                    options={REPAIR_REPORT_INSPECTION_RESULT_OPTIONS}
                    control={control}
                    errors={errors}
                    allowEmpty={false}
                    placeholder="結果"
                    containerClassName="w-full"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                    aria-label={`移除檢測項目 ${index + 1}`}
                    onClick={() => remove(index)}
                  >
                    <PiTrashBold className="size-4" />
                  </Button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </section>
  );
};

RepairReportInspectionItemsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default RepairReportInspectionItemsEditor;
